import React, { useMemo } from 'react';

import { TableCell, TableRow } from '@material-ui/core';
import { ReactComponent as DeleteIcon } from 'assets/icons/deleteIcon.svg';
import PropTypes from 'prop-types';

import { BasicTable } from 'components/DebFormComponents/Table/BasicTable';
import { ExpertTableInfoText } from 'components/ExpertTableInfoText/ExpertTableInfoText';
import { FormSection } from 'components/FormHelper/FormSection';
import { InfoTooltip } from 'components/InfoTooltip/InfoTooltip';
import { NE_ERROR } from 'components/NEForm/Utils/NEError';
import { TooltipIconButton } from 'components/TooltipIconButton/TooltipIconButton';

import { EIV_ENTRY_TYPE, TENANT_ID, TENANT_WITH_BIRTHDATE } from 'global/constants';

import { areExpertContactsEnabled } from 'utils/FormUtils';
import { isRegisteringInstallerGuestOnly } from 'utils/InstallerUtils';
import { getTenantId } from 'utils/tenantUtil';

import { CustomTableCell } from './CustomTableCell';

import './NEExpertsTable.sass';

export const NEExpertTable = ({ formik, branchSelectionValues, errors }) => {
  const getBranchTypeLabel = (branchType) => {
    if (branchType === EIV_ENTRY_TYPE.MAIN) return 'Stamm';
    if (branchType === EIV_ENTRY_TYPE.GUEST) return 'Gast';
    return '-';
  };

  const getBranchValidityDate = (isExpertActive, branchType, validityDate) => {
    if (!isExpertActive) return '-';
    if (branchType === EIV_ENTRY_TYPE.GUEST) return validityDate || '-';
    return '-';
  };

  const deleteExpert = (id) => {
    const newExperts = formik.values.experts.filter((expert) => expert.id !== id);
    formik.setFieldValue('experts', newExperts);
  };

  const headers = () => [
    <TableCell component="th" align="left" scope="row" key="firstName" className="data">
      <div className="ne-expert-table-cell__horizontal-flex">Name / Kontakt</div>
    </TableCell>,
    ...(TENANT_WITH_BIRTHDATE.includes(getTenantId())
      ? [
          <TableCell align="left" key="birthdate" className="data">
            <div className="ne-expert-table-cell__horizontal-flex">Geburtstag</div>
          </TableCell>,
        ]
      : []),
    ...(branchSelectionValues.isEnergyActive
      ? [
          <TableCell align="left" key="isExpertEnergyActive" className="data">
            <div className="ne-expert-table-cell__horizontal-flex">
              Strom / Gültig bis <br />
              (Status)
            </div>
          </TableCell>,
        ]
      : []),
    ...(branchSelectionValues.isGasActive
      ? [
          <CustomTableCell align="left" key="isExpertGasActive" className="data">
            <div className="ne-expert-table-cell__horizontal-flex">
              Gas / Gültig bis <br />
              (Status)
            </div>
          </CustomTableCell>,
        ]
      : []),
    ...(!isRegisteringInstallerGuestOnly(branchSelectionValues)
      ? [
          <TableCell align="left" key="isExpertOwner" className="data">
            <div className="ne-expert-table-cell__horizontal-flex">
              Inhaber
              <InfoTooltip title="auch Geschäftsführer*in, Gesellschafter*in, Teilhaber*in" />
            </div>
          </TableCell>,
        ]
      : []),
    <TableCell align="right" key="Button" className="action-buttons" />,
  ];

  const rows = useMemo(() => {
    const rowsNew = formik.values.experts.map((row) => (
      <TableRow
        key={row.id}
        sx={{
          '&:last-child td, &:last-child th': { border: 0 },
        }}>
        <TableCell scope="row" key="name" className="data expert-table__name-cell">
          <div className="expert-table__cell-container">
            <div className="expert-table__name-block">
              <span className="full-name">
                {`${row.lastName || ''} ${row.firstName || ''}`.trim()}
              </span>
              {areExpertContactsEnabled && row.telefon && (
                <span className="contact-line">{row.telefon}</span>
              )}
              {areExpertContactsEnabled && row.netzportalEmail && (
                <span className="contact-line">{row.netzportalEmail}</span>
              )}
            </div>
          </div>
        </TableCell>
        {getTenantId() === TENANT_ID.BAG ? (
          <CustomTableCell align="left" key="birthdate" className="data">
            {row.birthdate}
          </CustomTableCell>
        ) : null}
        {branchSelectionValues.isEnergyActive ? (
          <TableCell
            align="left"
            key="isExpertEnergyActive"
            className="data expert-table__status-cell">
            <div className="expert-table__cell-container">
              <div className="expert-table__status-lines">
                <span className="status-title">
                  {row.isExpertEnergyActive
                    ? getBranchTypeLabel(branchSelectionValues.branchTypeEnergy)
                    : '-'}
                </span>
                <span className="status-date">
                  {getBranchValidityDate(
                    row.isExpertEnergyActive,
                    branchSelectionValues.branchTypeEnergy,
                    row.energyValidityDate
                  )}
                </span>
                <span className="status-state">{row.isExpertEnergyActive ? '(Neu)' : '-'}</span>
              </div>
            </div>
          </TableCell>
        ) : null}
        {branchSelectionValues.isGasActive ? (
          <TableCell
            align="left"
            key="isExpertGasActive"
            className="data expert-table__status-cell">
            <div className="expert-table__cell-container">
              <div className="expert-table__status-lines">
                <span className="status-title">
                  {row.isExpertGasActive
                    ? getBranchTypeLabel(branchSelectionValues.branchTypeGas)
                    : '-'}
                </span>
                <span className="status-date">
                  {getBranchValidityDate(
                    row.isExpertGasActive,
                    branchSelectionValues.branchTypeGas,
                    row.gasValidityDate
                  )}
                </span>
                <span className="status-state">{row.isExpertGasActive ? '(Neu)' : '-'}</span>
              </div>
            </div>
          </TableCell>
        ) : null}
        {!isRegisteringInstallerGuestOnly(branchSelectionValues) ? (
          <CustomTableCell align="left" key="isExpertOwner" className="data">
            {row.isExpertOwner}
          </CustomTableCell>
        ) : null}
        <TableCell align="center" key="deleteButton" className="action-buttons">
          <div className="expert-table__button-container">
            <TooltipIconButton
              className="expert-table__icon-button"
              aria-label="Fachkraft löschen"
              onClick={() => {
                deleteExpert(row.id);
              }}>
              <DeleteIcon />
            </TooltipIconButton>
          </div>
        </TableCell>
      </TableRow>
    ));
    return rowsNew;
  }, [formik.values.experts, branchSelectionValues]);

  const showInfoText = useMemo(() => {
    if (formik.values.experts.length === 0) return true;

    const hasEnergyExpert = formik.values.experts.some((expert) => expert.isExpertEnergyActive);
    const hasGasExpert = formik.values.experts.some((expert) => expert.isExpertGasActive);

    const needsEnergyExpert = branchSelectionValues.isEnergyActive && !hasEnergyExpert;
    const needsGasExpert = branchSelectionValues.isGasActive && !hasGasExpert;

    return needsEnergyExpert || needsGasExpert;
  }, [
    formik.values.experts,
    branchSelectionValues.isEnergyActive,
    branchSelectionValues.isGasActive,
  ]);

  return (
    <div className="expert-table__wrapper">
      <FormSection
        infoText={
          <ExpertTableInfoText
            showInfoText={showInfoText}
            hasEnergy={branchSelectionValues.isEnergyActive}
            hasGas={branchSelectionValues.isGasActive}
            errors={errors}
            errorTypes={[NE_ERROR.MIN_EXPERT, NE_ERROR.MIN_BRANCH_EXPERT]}
            primaryErrorClass="ne-form__error-text"
            secondaryErrorClass={
              errors.includes(NE_ERROR.MIN_BRANCH_EXPERT) ? 'ne-form__error-text' : ''
            }
          />
        }>
        <BasicTable className="expert-table" headers={headers()} rows={rows} />
      </FormSection>
    </div>
  );
};

NEExpertTable.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.oneOf([Object.values(NE_ERROR)])).isRequired,
  formik: PropTypes.object.isRequired,
  branchSelectionValues: PropTypes.object.isRequired,
};
